/**
 * Three.js implementation of IRenderer
 * Renders entities as instanced spheres with orbit controls
 */

import * as THREE from 'three';
import type { IRenderer, RendererOptions } from '../types/renderer';
import { DEFAULT_RENDERER_OPTIONS } from '../types/renderer';

export class ThreeRenderer implements IRenderer {
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private renderer: THREE.WebGLRenderer | null = null;
  private instancedMesh: THREE.InstancedMesh | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private _initialized: boolean = false;

  private readonly options: Required<RendererOptions>;
  private readonly tempMatrix = new THREE.Matrix4();
  private readonly tempColor = new THREE.Color();

  // Orbit control state
  private isDragging: boolean = false;
  private prevX: number = 0;
  private prevY: number = 0;
  private theta: number = Math.PI / 4;
  private phi: number = Math.PI / 4;
  private radius: number = 300;

  constructor(options: RendererOptions = {}) {
    this.options = { ...DEFAULT_RENDERER_OPTIONS, ...options };
  }

  async initialize(canvas: HTMLCanvasElement): Promise<void> {
    if (this._initialized) {
      throw new Error('Renderer already initialized');
    }

    this.canvas = canvas;

    // Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(this.options.backgroundColor);

    // Camera
    const aspect = canvas.clientWidth / canvas.clientHeight;
    this.camera = new THREE.PerspectiveCamera(60, aspect, 1, 2000);
    this.updateCameraPosition();

    // WebGL Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
    });
    this.renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // Lighting
    const ambient = new THREE.AmbientLight(0x404040, 0.5);
    this.scene.add(ambient);

    const directional = new THREE.DirectionalLight(0xffffff, 1);
    directional.position.set(100, 200, 100);
    this.scene.add(directional);

    // Instanced spheres
    const geometry = new THREE.SphereGeometry(1, 16, 12);
    const material = new THREE.MeshPhongMaterial({
      color: 0x4488ff,
      flatShading: false,
    });

    this.instancedMesh = new THREE.InstancedMesh(
      geometry,
      material,
      this.options.maxEntities
    );
    this.instancedMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.instancedMesh.count = 0;
    this.scene.add(this.instancedMesh);

    // Setup orbit controls
    this.setupControls();

    this._initialized = true;
  }

  render(positions: Float32Array, entityCount: number): void {
    if (!this._initialized || !this.renderer || !this.scene || !this.camera || !this.instancedMesh) {
      return;
    }

    if (entityCount === 0) {
      this.instancedMesh.count = 0;
      this.renderer.render(this.scene, this.camera);
      return;
    }

    const count = Math.min(entityCount, this.options.maxEntities);

    // Update instance matrices and colors
    for (let i = 0; i < count; i++) {
      const x = positions[i * 3];
      const y = positions[i * 3 + 1];
      const z = positions[i * 3 + 2];

      // Scale and position
      this.tempMatrix.makeScale(
        this.options.sphereRadius,
        this.options.sphereRadius,
        this.options.sphereRadius
      );
      this.tempMatrix.setPosition(x, y, z);
      this.instancedMesh.setMatrixAt(i, this.tempMatrix);

      // Color by position (hue from angle)
      const hue = (Math.atan2(z, x) + Math.PI) / (2 * Math.PI);
      this.tempColor.setHSL(hue, 0.7, 0.5);
      this.instancedMesh.setColorAt(i, this.tempColor);
    }

    this.instancedMesh.count = count;
    this.instancedMesh.instanceMatrix.needsUpdate = true;
    if (this.instancedMesh.instanceColor) {
      this.instancedMesh.instanceColor.needsUpdate = true;
    }

    this.renderer.render(this.scene, this.camera);
  }

  resize(width: number, height: number): void {
    if (!this._initialized || !this.camera || !this.renderer) {
      return;
    }

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  dispose(): void {
    if (!this._initialized) {
      return;
    }

    this.removeControls();

    if (this.instancedMesh) {
      this.instancedMesh.geometry.dispose();
      (this.instancedMesh.material as THREE.Material).dispose();
      this.scene?.remove(this.instancedMesh);
    }

    this.renderer?.dispose();

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.instancedMesh = null;
    this.canvas = null;
    this._initialized = false;
  }

  isInitialized(): boolean {
    return this._initialized;
  }

  private updateCameraPosition(): void {
    if (!this.camera) return;

    this.camera.position.x = this.radius * Math.sin(this.phi) * Math.cos(this.theta);
    this.camera.position.y = this.radius * Math.cos(this.phi);
    this.camera.position.z = this.radius * Math.sin(this.phi) * Math.sin(this.theta);
    this.camera.lookAt(0, 0, 0);
  }

  private setupControls(): void {
    if (!this.canvas) return;

    this.canvas.addEventListener('mousedown', this.handleMouseDown);
    this.canvas.addEventListener('mousemove', this.handleMouseMove);
    this.canvas.addEventListener('mouseup', this.handleMouseUp);
    this.canvas.addEventListener('mouseleave', this.handleMouseUp);
    this.canvas.addEventListener('wheel', this.handleWheel, { passive: false });
  }

  private removeControls(): void {
    if (!this.canvas) return;

    this.canvas.removeEventListener('mousedown', this.handleMouseDown);
    this.canvas.removeEventListener('mousemove', this.handleMouseMove);
    this.canvas.removeEventListener('mouseup', this.handleMouseUp);
    this.canvas.removeEventListener('mouseleave', this.handleMouseUp);
    this.canvas.removeEventListener('wheel', this.handleWheel);
  }

  private handleMouseDown = (e: MouseEvent): void => {
    this.isDragging = true;
    this.prevX = e.clientX;
    this.prevY = e.clientY;
  };

  private handleMouseMove = (e: MouseEvent): void => {
    if (!this.isDragging) return;

    const dx = e.clientX - this.prevX;
    const dy = e.clientY - this.prevY;

    this.theta -= dx * 0.01;
    this.phi = Math.max(0.1, Math.min(Math.PI - 0.1, this.phi - dy * 0.01));

    this.prevX = e.clientX;
    this.prevY = e.clientY;

    this.updateCameraPosition();
  };

  private handleMouseUp = (): void => {
    this.isDragging = false;
  };

  private handleWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.radius = Math.max(50, Math.min(1000, this.radius + e.deltaY * 0.5));
    this.updateCameraPosition();
  };
}
